import {
  createContext, useCallback, useContext, useState, type ReactNode,
} from 'react';

export type CertificateDateOrigin = 'default' | 'manual' | 'calculated' | 'ai';

type CertificateDateField = 'lastReceivedOn' | 'expiryDate';

export type CertificateDateSource = CertificateDateField | `certificationsByAssetId.${string}.${CertificateDateField}`;

type CertificateDateOrigins = Partial<Record<CertificateDateSource, Exclude<CertificateDateOrigin, 'default'>>>;

type CertificateDateOriginContextValue = {
  getOrigin: (source: CertificateDateSource) => CertificateDateOrigin;
  setOrigin: (source: CertificateDateSource, origin: CertificateDateOrigin) => void;
  resetOrigins: (sources: CertificateDateSource[]) => void;
};

const CertificateDateOriginContext = createContext<CertificateDateOriginContextValue>({
  getOrigin: () => 'default',
  setOrigin: () => undefined,
  resetOrigins: () => undefined,
});

export const isCertificateDateSource = (source: string): source is CertificateDateSource => {

  return source === 'lastReceivedOn'
    || source === 'expiryDate'
    || /^certificationsByAssetId\.[^.]+\.(lastReceivedOn|expiryDate)$/.test(source);

};

export const CertificateDateOriginProvider = (props: { children: ReactNode }) => {

  const [origins, setOrigins] = useState<CertificateDateOrigins | null>(null);

  const getOrigin = useCallback(
    (source: CertificateDateSource) => origins?.[source] ?? 'default',
    [origins],
  );

  const setOrigin = useCallback((source: CertificateDateSource, origin: CertificateDateOrigin) => {

    setOrigins((current) => {

      if (origin === 'default') {

        if (current?.[source] === undefined) return current;

        const next = { ...current };
        delete next[source];
        return next;

      }

      if (current?.[source] === origin) return current;

      return { ...current, [source]: origin };

    });

  }, []);

  const resetOrigins = useCallback((sources?: CertificateDateSource[]) => {

    if (sources) {

      setOrigins((current) => {

        return sources.reduce((accumulator, source) => {

          if (current?.[source] === undefined) return accumulator;

          const next = { ...accumulator };
          delete next[source];
          return next;

        }, current);

      });

    } else {

      setOrigins(null);

    }

  }, []);

  return (
    <CertificateDateOriginContext.Provider value={{ getOrigin, setOrigin, resetOrigins }}>
      {props.children}
    </CertificateDateOriginContext.Provider>
  );

};

export const useCertificateDateOriginsContext = () => useContext(CertificateDateOriginContext);
